import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { buildLinkedInOAuthUrl } from '../../config/socialMediaConfig';
import './LinkedInBusiness.css';

interface LinkedInBusinessProps {
  storeId: string;
  storeName: string;
}

type ActiveSection = 'connection' | 'posts' | 'analytics' | 'campaigns' | 'leads' | 'audiences';

interface ConnectionInfo {
  connected: boolean;
  status: string;
  profile: { name?: string; localizedName?: string; email?: string; picture?: string; logoUrl?: string } | null;
  organizationId: string | null;
  connectedAt: string | null;
  tokenExpiresAt: string | null;
}

interface AnalyticsData {
  period: string;
  totalFollowers: number;
  organicFollowers: number;
  paidFollowers: number;
  impressions: number;
  uniqueImpressions: number;
  clicks: number;
  likes: number;
  comments: number;
  shares: number;
  engagementRate: number;
  dailyMetrics: Array<{ date: string; impressions: number; clicks: number; likes: number; comments: number; shares: number }>;
}

interface DemographicsData {
  [key: string]: Array<{ name: string; organicCount: number; paidCount: number; totalCount: number }>;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
  objectiveType: string;
  dailyBudget: { amount: string; currencyCode: string } | null;
  totalBudget: { amount: string; currencyCode: string } | null;
  localMetrics?: { impressions: number; clicks: number; spend: number; conversions: number };
}

interface CampaignMetrics {
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  conversions: number;
  cpc: number;
  cpm: number;
  dailyMetrics: Array<{ date: string; impressions: number; clicks: number; spend: number }>;
}

interface LeadForm {
  id: string;
  name: string;
  status: string;
  createdAt: string;
}

interface Lead {
  leadId: string;
  formId: string;
  answers: Record<string, string>;
  submittedAt: string;
  leadScore: number;
}

interface Audience {
  id: string;
  name: string;
  type: string;
  status: string;
  matchedCount: number;
  uploadedCount: number;
  matchRate: string;
  createdAt: string;
}

export const LinkedInBusiness: React.FC<LinkedInBusinessProps> = ({ storeId, storeName }) => {
  const { getIdToken } = useAuth();
  const [activeSection, setActiveSection] = useState<ActiveSection>('connection');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Connection state
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);

  // Post composer state
  const [postText, setPostText] = useState('');
  const [postImageUrls, setPostImageUrls] = useState('');
  const [postArticleUrl, setPostArticleUrl] = useState('');
  const [postArticleTitle, setPostArticleTitle] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [demographics, setDemographics] = useState<DemographicsData | null>(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');

  // Campaigns state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetrics | null>(null);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', objectiveType: 'BRAND_AWARENESS', dailyBudget: '', status: 'PAUSED' });

  // Leads state
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);

  // Audiences state
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [showCreateAudience, setShowCreateAudience] = useState(false);
  const [newAudience, setNewAudience] = useState({ name: '', emails: '' });

  const fetchConnectionStatus = useCallback(async () => {
    if (!storeId) return;
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinStatus(storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          setConnectionInfo(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch LinkedIn status:', err);
    }
  }, [storeId, getIdToken]);

  useEffect(() => {
    console.log('LinkedInBusiness mounted', { storeId, storeName });
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  const handleConnect = () => {
    try {
      console.log('LinkedIn handleConnect called', { storeId, storeName });
      const oauthState = JSON.stringify({ storeId, storeName, timestamp: Date.now() });
      localStorage.setItem('linkedin_oauth_state', oauthState);
      const oauthUrl = buildLinkedInOAuthUrl(storeId, storeName);
      console.log('LinkedIn OAuth URL:', oauthUrl);
      window.location.href = oauthUrl;
    } catch (err) {
      console.error('LinkedIn handleConnect error:', err);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect LinkedIn?')) return;
    try {
      const token = await getIdToken();
      await fetch(API_ENDPOINTS.linkedinDisconnect(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ storeId })
      });
      setConnectionInfo(null);
      setSuccess('LinkedIn disconnected');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to disconnect LinkedIn');
    }
  };

  const handlePublishPost = async () => {
    if (!postText.trim()) return;
    setPublishing(true);
    setError(null);
    try {
      const token = await getIdToken();
      const body: any = { storeId, text: postText };
      if (postImageUrls.trim()) {
        body.imageUrls = postImageUrls.split('\n').map(u => u.trim()).filter(Boolean);
      }
      if (postArticleUrl.trim()) {
        body.articleUrl = postArticleUrl;
        body.articleTitle = postArticleTitle;
      }
      const resp = await fetch(API_ENDPOINTS.linkedinPost(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess('Post published to LinkedIn');
        setPostText('');
        setPostImageUrls('');
        setPostArticleUrl('');
        setPostArticleTitle('');
        fetchPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to publish post');
      }
    } catch (err) {
      setError('Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  const fetchPosts = useCallback(async () => {
    if (!storeId || !connectionInfo?.connected) return;
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinPosts(storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.success) setPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  }, [storeId, connectionInfo, getIdToken]);

  const fetchAnalytics = useCallback(async () => {
    if (!storeId || !connectionInfo?.connected) return;
    setLoading(true);
    try {
      const token = await getIdToken();
      const [analyticsResp, demoResp] = await Promise.all([
        fetch(`${API_ENDPOINTS.linkedinAnalytics(storeId)}&period=${analyticsPeriod}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.linkedinDemographics(storeId), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const analyticsJson = await analyticsResp.json();
      const demoJson = await demoResp.json();
      if (analyticsJson.success) setAnalyticsData(analyticsJson.analytics);
      if (demoJson.success) setDemographics(demoJson.demographics);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId, connectionInfo, analyticsPeriod, getIdToken]);

  const fetchCampaigns = useCallback(async () => {
    if (!storeId || !connectionInfo?.connected) return;
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinCampaigns(storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.success) setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
  }, [storeId, connectionInfo, getIdToken]);

  const fetchCampaignMetrics = async (campaignId: string) => {
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinCampaignMetrics(campaignId, storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.success) {
        setCampaignMetrics(data.metrics);
        setSelectedCampaign(campaignId);
      }
    } catch (err) {
      console.error('Failed to fetch campaign metrics:', err);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) return;
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinCampaigns(storeId).replace(`?storeId=${storeId}`, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          storeId,
          name: newCampaign.name,
          objectiveType: newCampaign.objectiveType,
          dailyBudget: newCampaign.dailyBudget ? parseFloat(newCampaign.dailyBudget) : undefined,
          status: newCampaign.status
        })
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess('Campaign created');
        setShowCreateCampaign(false);
        setNewCampaign({ name: '', objectiveType: 'BRAND_AWARENESS', dailyBudget: '', status: 'PAUSED' });
        fetchCampaigns();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to create campaign');
      }
    } catch (err) {
      setError('Failed to create campaign');
    }
  };

  const handleToggleCampaignStatus = async (campaign: Campaign) => {
    const newStatus = campaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      const token = await getIdToken();
      await fetch(API_ENDPOINTS.linkedinCampaign(campaign.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ storeId, status: newStatus })
      });
      fetchCampaigns();
    } catch (err) {
      setError('Failed to update campaign');
    }
  };

  const fetchLeads = useCallback(async () => {
    if (!storeId || !connectionInfo?.connected) return;
    try {
      const token = await getIdToken();
      const [formsResp, leadsResp] = await Promise.all([
        fetch(API_ENDPOINTS.linkedinLeadForms(storeId), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.linkedinLeads(storeId), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const formsData = await formsResp.json();
      const leadsData = await leadsResp.json();
      if (formsData.success) setLeadForms(formsData.forms || []);
      if (leadsData.success) setLeads(leadsData.leads || []);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  }, [storeId, connectionInfo, getIdToken]);

  const handleExportLeads = async () => {
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinLeadsExport(storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const csvText = await resp.text();
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'linkedin-leads.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export leads');
    }
  };

  const fetchAudiences = useCallback(async () => {
    if (!storeId || !connectionInfo?.connected) return;
    try {
      const token = await getIdToken();
      const resp = await fetch(API_ENDPOINTS.linkedinAudiences(storeId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.success) setAudiences(data.audiences || []);
    } catch (err) {
      console.error('Failed to fetch audiences:', err);
    }
  }, [storeId, connectionInfo, getIdToken]);

  const handleCreateAudience = async () => {
    if (!newAudience.name.trim()) return;
    try {
      const token = await getIdToken();
      const body: any = { storeId, name: newAudience.name };
      if (newAudience.emails.trim()) {
        body.emails = newAudience.emails.split('\n').map(e => e.trim()).filter(Boolean);
        body.type = 'USER_UPLOADED';
      }
      const resp = await fetch(API_ENDPOINTS.linkedinAudiences(storeId).replace(`?storeId=${storeId}`, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess('Audience created');
        setShowCreateAudience(false);
        setNewAudience({ name: '', emails: '' });
        fetchAudiences();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to create audience');
      }
    } catch (err) {
      setError('Failed to create audience');
    }
  };

  const handleDeleteAudience = async (audienceId: string) => {
    if (!window.confirm('Delete this audience?')) return;
    try {
      const token = await getIdToken();
      await fetch(`${API_ENDPOINTS.linkedinAudience(audienceId)}?storeId=${storeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAudiences();
    } catch (err) {
      setError('Failed to delete audience');
    }
  };

  // Fetch data when section changes
  useEffect(() => {
    if (!connectionInfo?.connected) return;
    switch (activeSection) {
      case 'posts': fetchPosts(); break;
      case 'analytics': fetchAnalytics(); break;
      case 'campaigns': fetchCampaigns(); break;
      case 'leads': fetchLeads(); break;
      case 'audiences': fetchAudiences(); break;
    }
  }, [activeSection, connectionInfo, fetchPosts, fetchAnalytics, fetchCampaigns, fetchLeads, fetchAudiences]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#2e7d32';
    if (score >= 50) return '#ed6c02';
    return '#d32f2f';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Hot';
    if (score >= 50) return 'Warm';
    return 'Cold';
  };

  return (
    <div className="linkedin-business-container">
      <div className="linkedin-business-header">
        <h2>LinkedIn Business</h2>
        <p>Manage your LinkedIn presence, publish content, run ads, and track leads</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>x</button>
        </div>
      )}

      <div className="linkedin-business-nav">
        <button className={`nav-btn ${activeSection === 'connection' ? 'active' : ''}`} onClick={() => setActiveSection('connection')}>
          <i className="fas fa-link"></i> Connection
        </button>
        <button className={`nav-btn ${activeSection === 'posts' ? 'active' : ''}`} onClick={() => setActiveSection('posts')} disabled={!connectionInfo?.connected}>
          <i className="fas fa-edit"></i> Posts
        </button>
        <button className={`nav-btn ${activeSection === 'analytics' ? 'active' : ''}`} onClick={() => setActiveSection('analytics')} disabled={!connectionInfo?.connected}>
          <i className="fas fa-chart-bar"></i> Analytics
        </button>
        <button className={`nav-btn ${activeSection === 'campaigns' ? 'active' : ''}`} onClick={() => setActiveSection('campaigns')} disabled={!connectionInfo?.connected}>
          <i className="fas fa-bullhorn"></i> Campaigns
        </button>
        <button className={`nav-btn ${activeSection === 'leads' ? 'active' : ''}`} onClick={() => setActiveSection('leads')} disabled={!connectionInfo?.connected}>
          <i className="fas fa-user-plus"></i> Leads
        </button>
        <button className={`nav-btn ${activeSection === 'audiences' ? 'active' : ''}`} onClick={() => setActiveSection('audiences')} disabled={!connectionInfo?.connected}>
          <i className="fas fa-users"></i> Audiences
        </button>
      </div>

      <div className="linkedin-business-content">
        {/* Connection Section */}
        {activeSection === 'connection' && (
          <div className="connection-section">
            {connectionInfo?.connected ? (
              <div className="connected-state">
                <div className="connection-badge">
                  <i className="fab fa-linkedin" style={{ color: '#0A66C2', fontSize: '2rem' }}></i>
                  <div>
                    <h3>{connectionInfo.profile?.name || connectionInfo.profile?.localizedName || 'LinkedIn Account'}</h3>
                    <p className="connection-status-text">Connected {connectionInfo.connectedAt ? `on ${new Date(connectionInfo.connectedAt).toLocaleDateString()}` : ''}</p>
                    {connectionInfo.organizationId && <span className="org-badge">Organization: {connectionInfo.organizationId}</span>}
                  </div>
                </div>
                <button onClick={handleDisconnect} className="disconnect-btn">Disconnect</button>
              </div>
            ) : (
              <div className="disconnected-state">
                <div className="connect-prompt">
                  <i className="fab fa-linkedin" style={{ fontSize: '4rem', color: '#0A66C2' }}></i>
                  <h3>Connect Your LinkedIn Account</h3>
                  <p>Link your LinkedIn company page to publish content, manage ads, track analytics, and capture leads.</p>
                  <button onClick={handleConnect} className="connect-btn">
                    <i className="fab fa-linkedin-in"></i> Connect LinkedIn
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Posts Section */}
        {activeSection === 'posts' && connectionInfo?.connected && (
          <div className="posts-section">
            <div className="post-composer">
              <h3>Create Post</h3>
              <textarea
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                placeholder="Write your LinkedIn post..."
                rows={4}
                className="post-textarea"
              />
              <div className="post-options">
                <div className="post-option-group">
                  <label>Image URLs (one per line)</label>
                  <textarea
                    value={postImageUrls}
                    onChange={(e) => setPostImageUrls(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    rows={2}
                    className="post-input-small"
                  />
                </div>
                <div className="post-option-group">
                  <label>Article Link (optional)</label>
                  <input
                    type="url"
                    value={postArticleUrl}
                    onChange={(e) => setPostArticleUrl(e.target.value)}
                    placeholder="https://example.com/article"
                    className="post-input"
                  />
                  {postArticleUrl && (
                    <input
                      type="text"
                      value={postArticleTitle}
                      onChange={(e) => setPostArticleTitle(e.target.value)}
                      placeholder="Article title"
                      className="post-input"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              </div>
              <button onClick={handlePublishPost} disabled={publishing || !postText.trim()} className="publish-btn">
                {publishing ? 'Publishing...' : 'Publish to LinkedIn'}
              </button>
            </div>

            <div className="posts-feed">
              <h3>Recent Posts</h3>
              {posts.length === 0 ? (
                <p className="empty-state">No posts yet</p>
              ) : (
                posts.map((post, idx) => (
                  <div key={post.id || idx} className="post-card">
                    <p className="post-text">{post.text || post.content || post.commentary || ''}</p>
                    <div className="post-meta">
                      <span>{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ''}</span>
                      {post.metrics && (
                        <div className="post-metrics">
                          <span>{post.metrics.likes || 0} likes</span>
                          <span>{post.metrics.comments || 0} comments</span>
                          <span>{post.metrics.shares || 0} shares</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Analytics Section */}
        {activeSection === 'analytics' && connectionInfo?.connected && (
          <div className="analytics-section">
            <div className="analytics-header">
              <h3>Page Analytics</h3>
              <div className="period-selector">
                {['7d', '30d', '90d'].map(p => (
                  <button key={p} className={`period-btn ${analyticsPeriod === p ? 'active' : ''}`} onClick={() => setAnalyticsPeriod(p)}>
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="loading-state">Loading analytics...</div>
            ) : analyticsData ? (
              <>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.totalFollowers.toLocaleString()}</div>
                    <div className="metric-label">Total Followers</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.impressions.toLocaleString()}</div>
                    <div className="metric-label">Impressions</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.clicks.toLocaleString()}</div>
                    <div className="metric-label">Clicks</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.engagementRate}%</div>
                    <div className="metric-label">Engagement Rate</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.likes.toLocaleString()}</div>
                    <div className="metric-label">Likes</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.comments.toLocaleString()}</div>
                    <div className="metric-label">Comments</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.shares.toLocaleString()}</div>
                    <div className="metric-label">Shares</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{analyticsData.uniqueImpressions.toLocaleString()}</div>
                    <div className="metric-label">Unique Impressions</div>
                  </div>
                </div>

                {demographics && (
                  <div className="demographics-section">
                    <h3>Follower Demographics</h3>
                    <div className="demographics-grid">
                      {Object.entries(demographics).map(([key, items]) => (
                        items.length > 0 && (
                          <div key={key} className="demo-card">
                            <h4>{key === 'geo' ? 'Location' : key === 'staffCountRange' ? 'Company Size' : key.charAt(0).toUpperCase() + key.slice(1)}</h4>
                            <div className="demo-list">
                              {items.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="demo-item">
                                  <span className="demo-name">{item.name}</span>
                                  <span className="demo-count">{item.totalCount.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="empty-state">No analytics data available. Connect a LinkedIn organization page to see analytics.</p>
            )}
          </div>
        )}

        {/* Campaigns Section */}
        {activeSection === 'campaigns' && connectionInfo?.connected && (
          <div className="campaigns-section">
            <div className="section-header">
              <h3>Ad Campaigns</h3>
              <button onClick={() => setShowCreateCampaign(!showCreateCampaign)} className="action-btn">
                {showCreateCampaign ? 'Cancel' : 'Create Campaign'}
              </button>
            </div>

            {showCreateCampaign && (
              <div className="create-form">
                <div className="form-group">
                  <label>Campaign Name</label>
                  <input type="text" value={newCampaign.name} onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} placeholder="Campaign name" className="form-input" />
                </div>
                <div className="form-group">
                  <label>Objective</label>
                  <select value={newCampaign.objectiveType} onChange={(e) => setNewCampaign({ ...newCampaign, objectiveType: e.target.value })} className="form-select">
                    <option value="BRAND_AWARENESS">Brand Awareness</option>
                    <option value="WEBSITE_VISITS">Website Visits</option>
                    <option value="ENGAGEMENT">Engagement</option>
                    <option value="VIDEO_VIEWS">Video Views</option>
                    <option value="LEAD_GENERATION">Lead Generation</option>
                    <option value="WEBSITE_CONVERSIONS">Website Conversions</option>
                    <option value="JOB_APPLICANTS">Job Applicants</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Daily Budget (USD)</label>
                  <input type="number" value={newCampaign.dailyBudget} onChange={(e) => setNewCampaign({ ...newCampaign, dailyBudget: e.target.value })} placeholder="50.00" className="form-input" />
                </div>
                <button onClick={handleCreateCampaign} className="submit-btn">Create Campaign</button>
              </div>
            )}

            {campaigns.length === 0 ? (
              <p className="empty-state">No campaigns found</p>
            ) : (
              <div className="campaigns-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Objective</th>
                      <th>Budget</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(campaign => (
                      <tr key={campaign.id}>
                        <td>{campaign.name}</td>
                        <td>
                          <span className={`status-badge ${campaign.status.toLowerCase()}`}>{campaign.status}</span>
                        </td>
                        <td>{campaign.objectiveType}</td>
                        <td>{campaign.dailyBudget ? `$${campaign.dailyBudget.amount}/day` : '-'}</td>
                        <td className="campaign-actions">
                          <button onClick={() => handleToggleCampaignStatus(campaign)} className="action-btn-small">
                            {campaign.status === 'ACTIVE' ? 'Pause' : 'Activate'}
                          </button>
                          <button onClick={() => fetchCampaignMetrics(campaign.id)} className="action-btn-small secondary">
                            Metrics
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedCampaign && campaignMetrics && (
              <div className="campaign-metrics-panel">
                <h4>Campaign Performance</h4>
                <div className="metrics-grid compact">
                  <div className="metric-card small">
                    <div className="metric-value">{campaignMetrics.impressions.toLocaleString()}</div>
                    <div className="metric-label">Impressions</div>
                  </div>
                  <div className="metric-card small">
                    <div className="metric-value">{campaignMetrics.clicks.toLocaleString()}</div>
                    <div className="metric-label">Clicks</div>
                  </div>
                  <div className="metric-card small">
                    <div className="metric-value">{campaignMetrics.ctr}%</div>
                    <div className="metric-label">CTR</div>
                  </div>
                  <div className="metric-card small">
                    <div className="metric-value">${campaignMetrics.spend.toFixed(2)}</div>
                    <div className="metric-label">Spend</div>
                  </div>
                  <div className="metric-card small">
                    <div className="metric-value">${campaignMetrics.cpc}</div>
                    <div className="metric-label">CPC</div>
                  </div>
                  <div className="metric-card small">
                    <div className="metric-value">{campaignMetrics.conversions}</div>
                    <div className="metric-label">Conversions</div>
                  </div>
                </div>
                <button onClick={() => { setSelectedCampaign(null); setCampaignMetrics(null); }} className="close-btn">Close</button>
              </div>
            )}
          </div>
        )}

        {/* Leads Section */}
        {activeSection === 'leads' && connectionInfo?.connected && (
          <div className="leads-section">
            <div className="section-header">
              <h3>Lead Generation</h3>
              <button onClick={handleExportLeads} className="action-btn">Export CSV</button>
            </div>

            {leadForms.length > 0 && (
              <div className="lead-forms-list">
                <h4>Lead Forms</h4>
                <div className="forms-grid">
                  {leadForms.map(form => (
                    <div key={form.id} className="form-card">
                      <strong>{form.name}</strong>
                      <span className={`status-badge ${form.status?.toLowerCase()}`}>{form.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="score-legend">
              <span className="legend-item hot">80-100: Hot</span>
              <span className="legend-item warm">50-79: Warm</span>
              <span className="legend-item cold">0-49: Cold</span>
            </div>

            {leads.length === 0 ? (
              <p className="empty-state">No leads captured yet</p>
            ) : (
              <div className="leads-table">
                <table>
                  <thead>
                    <tr>
                      <th>Score</th>
                      <th>Form</th>
                      <th>Details</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => (
                      <tr key={lead.leadId}>
                        <td>
                          <span className="lead-score" style={{ backgroundColor: getScoreColor(lead.leadScore), color: '#fff' }}>
                            {lead.leadScore} - {getScoreLabel(lead.leadScore)}
                          </span>
                        </td>
                        <td>{lead.formId || '-'}</td>
                        <td>
                          <div className="lead-details">
                            {Object.entries(lead.answers || {}).slice(0, 3).map(([key, val]) => (
                              <div key={key} className="lead-detail-item">
                                <span className="detail-key">{key}:</span> {val}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td>{lead.submittedAt ? new Date(lead.submittedAt).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Audiences Section */}
        {activeSection === 'audiences' && connectionInfo?.connected && (
          <div className="audiences-section">
            <div className="section-header">
              <h3>Matched Audiences</h3>
              <button onClick={() => setShowCreateAudience(!showCreateAudience)} className="action-btn">
                {showCreateAudience ? 'Cancel' : 'Create Audience'}
              </button>
            </div>

            {showCreateAudience && (
              <div className="create-form">
                <div className="form-group">
                  <label>Audience Name</label>
                  <input type="text" value={newAudience.name} onChange={(e) => setNewAudience({ ...newAudience, name: e.target.value })} placeholder="Audience name" className="form-input" />
                </div>
                <div className="form-group">
                  <label>Email List (one per line)</label>
                  <textarea value={newAudience.emails} onChange={(e) => setNewAudience({ ...newAudience, emails: e.target.value })} placeholder="email@example.com" rows={4} className="form-textarea" />
                </div>
                <button onClick={handleCreateAudience} className="submit-btn">Create Audience</button>
              </div>
            )}

            {audiences.length === 0 ? (
              <p className="empty-state">No audiences found</p>
            ) : (
              <div className="audiences-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Match Rate</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audiences.map(audience => (
                      <tr key={audience.id}>
                        <td>{audience.name}</td>
                        <td>{audience.type}</td>
                        <td>{audience.matchedCount.toLocaleString()}</td>
                        <td>{audience.matchRate}%</td>
                        <td><span className={`status-badge ${audience.status?.toLowerCase()}`}>{audience.status}</span></td>
                        <td>
                          <button onClick={() => handleDeleteAudience(audience.id)} className="action-btn-small danger">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
